import { Trans } from '@i18n';
import { Checkbox, FormControlLabel, FormGroup, Stack } from '@mui/material';
import { BuildingActiveStateFilter } from '@predium/enums';
import { memo, useCallback } from 'react';
import Collapsible from '../../common/Collapsible';
import { useBuildingFilters } from '../BuildingFiltersProvider';

/**
 * Renders the "Building system status" filter section. The active/inactive status is
 * controlled by a single enum (BOTH | ONLY_ACTIVE | ONLY_INACTIVE), surfaced here as two
 * mutually exclusive options. No selection means BOTH (show all).
 */
const BuildingSystemStatusFilter = function BuildingSystemStatusFilter() {
  const { filters, setFilters } = useBuildingFilters();

  const activeStateFilter = filters.activeStateFilter ?? BuildingActiveStateFilter.BOTH;

  const handleToggle = useCallback(
    (value: BuildingActiveStateFilter.ONLY_ACTIVE | BuildingActiveStateFilter.ONLY_INACTIVE, isChecked: boolean) => {
      void setFilters((prev) => ({
        ...prev,
        // Toggling an option off falls back to BOTH (no filter); the options are mutually exclusive.
        activeStateFilter: isChecked ? value : null,
      }));
    },
    [setFilters],
  );

  return (
    <Collapsible title={<Trans i18nKey="DataCollection_BuildingListFilter-buildingSystemStatus" />}>
      <Stack spacing={1} mt={-1}>
        <FormGroup
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
            gap: 1,
            width: '100%',
          }}
        >
          <FormControlLabel
            control={
              <Checkbox
                checked={activeStateFilter === BuildingActiveStateFilter.ONLY_ACTIVE}
                onChange={(e) => handleToggle(BuildingActiveStateFilter.ONLY_ACTIVE, e.target.checked)}
              />
            }
            label={<Trans i18nKey="DataCollection_BuildingListFilter-hideInactiveBuildings" />}
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={activeStateFilter === BuildingActiveStateFilter.ONLY_INACTIVE}
                onChange={(e) => handleToggle(BuildingActiveStateFilter.ONLY_INACTIVE, e.target.checked)}
              />
            }
            label={<Trans i18nKey="DataCollection_BuildingListFilter-showOnlyInactiveBuildings" />}
          />
        </FormGroup>
      </Stack>
    </Collapsible>
  );
};

export default memo(BuildingSystemStatusFilter);
